/**
 * Deterministic TeamBuilder AI advisor content.
 * All messages are generated from controlled templates + the user's assessment
 * answers. No AI calls, no invented facts, no benchmark/outcome claims.
 */

const HEALTHCARE_PAINS = [
  "insurance_verification", "prior_auth", "patient_referrals", "medical_admin",
];

const labelOf = (list, key) => {
  const found = (list || []).find((x) => x.key === key);
  return found ? found.label : key;
};

const NUM_WORDS = { 1: "single", 2: "two-role", 3: "three-role", 4: "four-role", 5: "five-role" };

export const FINAL_STAGES = [
  "Reviewing your business profile",
  "Mapping workload to role capacity",
  "Matching pain points to specialized roles",
  "Prioritizing your recommended team",
  "Sizing your dedicated team",
  "Aligning roles to your priorities",
  "Generating your Workforce Opportunity Analysis",
  "Preparing your implementation roadmap",
];

export const METHOD_NOTE =
  "TeamBuilder AI combines your assessment responses with Rendeavored's configurable " +
  "staffing rules, role library, workload assumptions, and AI-assisted narrative analysis.";

// ---- Advisor acknowledgement per step ----
const INDUSTRY_MSG = {
  healthcare:
    "Healthcare operations often require specialized administrative support because scheduling, documentation, patient communication, and payer workflows create different staffing needs than general business administration.",
  technology:
    "Technology teams often benefit from reliable service-desk and coordination capacity so engineers can stay focused on product work.",
  professional_services:
    "Professional services firms tend to gain the most from executive and administrative leverage that protects billable time.",
  ecommerce_retail:
    "E-commerce operations usually concentrate demand in customer experience and marketing execution.",
  real_estate:
    "Real estate teams often see the biggest impact from consistent lead follow-up and transaction coordination.",
  finance:
    "Finance-oriented businesses typically prioritize accurate bookkeeping and dependable back-office support.",
  marketing_agency:
    "Agencies generally need consistent production and project coordination to protect delivery quality.",
  other:
    "I'll focus on the support functions that most commonly create leverage for teams like yours.",
};

const SIZE_MSG = {
  "1-10": "At your size, a single versatile support hire can often remove the most friction quickly.",
  "11-50": "At this company size, role specialization can often improve consistency without requiring a large internal department.",
  "51-200": "At this scale, dedicated roles tend to outperform shared responsibilities for consistency and accountability.",
  "201-500": "At your scale, structured, specialized support roles are usually more effective than stretched generalists.",
  "500+": "At your scale, structured, specialized support roles are usually more effective than stretched generalists.",
};

const WORKLOAD_MSG = {
  low: "A lighter repetitive workload may be well served by shared or part-time support.",
  medium: "This level of repetitive work is often enough to justify a dedicated support role.",
  high: "That workload is approaching the capacity of one full-time support role. I'll weigh whether a generalist or specialized team would be more effective.",
  very_high: "This volume typically exceeds a single role, so I'll consider whether multiple dedicated roles are warranted.",
};

const GROWTH_MSG = {
  maintain: "With stabilization as the priority, the first recommendation will emphasize workload relief and consistency.",
  moderate: "For measured growth, I'll balance immediate relief with roles that support steady expansion.",
  aggressive: "For aggressive growth, I'll prioritize a phased team that can scale with demand.",
};

export function getAdvisorMessage(stepKey, answers) {
  switch (stepKey) {
    case "industry":
      return answers.industry ? INDUSTRY_MSG[answers.industry] : null;
    case "company":
      return answers.company_size ? SIZE_MSG[answers.company_size] : null;
    case "workload":
      return answers.workload ? WORKLOAD_MSG[answers.workload] : null;
    case "pains": {
      const p = answers.pain_points || [];
      if (p.includes("admin_overload") && p.includes("scheduling"))
        return "I'm seeing a strong connection between administrative workload and scheduling pressure. Those functions may be candidates for consolidation under one primary support role.";
      if (p.some((x) => HEALTHCARE_PAINS.includes(x)))
        return "Your payer- and patient-workflow pressures point toward specialized healthcare administrative support.";
      if (p.length >= 1)
        return "I'll map each pain point to the roles that most directly relieve it.";
      return null;
    }
    case "goals":
      return answers.growth_goal ? GROWTH_MSG[answers.growth_goal] : null;
    default:
      return null;
  }
}

// ---- Contextual transition messages ----
export function getTransition(stepKey, answers) {
  switch (stepKey) {
    case "industry":
      return answers.industry === "healthcare"
        ? "Reviewing healthcare-specific staffing requirements\u2026"
        : "Reviewing your industry profile\u2026";
    case "company":
      return "Right-sizing the recommendation for your company\u2026";
    case "workload":
      if (answers.workload === "low") return "Testing whether shared or part-time support may be sufficient\u2026";
      if (answers.workload === "very_high") return "Evaluating whether the workload requires multiple dedicated roles\u2026";
      return "Evaluating workload capacity\u2026";
    case "pains": {
      const p = answers.pain_points || [];
      if (p.includes("sales_followup")) return "Assessing where lead response and qualification support fit into your team\u2026";
      if (p.includes("prior_auth")) return "Evaluating specialized payer-workflow support\u2026";
      return "Connecting your operational pain points\u2026";
    }
    case "goals":
      return answers.growth_goal === "aggressive"
        ? "Building a phased team that can scale with demand\u2026"
        : "Assessing your growth and implementation timeline\u2026";
    default:
      return "Analyzing your responses\u2026";
  }
}

// ---- Emerging insight per step (one at a time) ----
export function getEmergingInsight(stepKey, answers) {
  const p = answers.pain_points || [];
  if (stepKey === "workload" && (answers.workload === "high" || answers.workload === "very_high"))
    return "Your repetitive workload is large enough to justify dedicated ownership rather than distributing tasks across current staff.";
  if (stepKey === "pains") {
    const hasHealthcare = p.some((x) => HEALTHCARE_PAINS.includes(x));
    const hasGeneral = p.some((x) => !HEALTHCARE_PAINS.includes(x));
    if (hasHealthcare && hasGeneral)
      return "Your selected pain points span both general operations and healthcare administration, so a blended team may be more appropriate than a single generalist.";
    if (p.length >= 3)
      return "You've identified several pressure points, which suggests distributing responsibilities across a small, focused team.";
  }
  if (stepKey === "goals" && answers.growth_goal === "maintain")
    return "Because your priority is stabilization rather than aggressive growth, the first recommendation should emphasize workload relief and consistency.";
  if (stepKey === "goals" && answers.growth_goal === "aggressive")
    return "With aggressive growth as the goal, sequencing roles in phases will help you scale capacity without overbuilding on day one.";
  return null;
}

// ---- "What I've learned" factors ----
export function getFactors(answers, config) {
  const factors = [];
  if (answers.industry) factors.push(labelOf(config.industries, answers.industry));
  if (answers.company_size) factors.push(`${answers.company_size} employees`);
  if (answers.workload) factors.push(labelOf(config.workloads, answers.workload) + " of repetitive work");
  if ((answers.pain_points || []).length) {
    const labels = answers.pain_points.map((k) => labelOf(config.pain_points, k));
    factors.push("Primary pressure: " + labels.slice(0, 2).join(" & ").toLowerCase());
  }
  if (answers.growth_goal) factors.push("Goal: " + labelOf(config.growth_goals, answers.growth_goal).toLowerCase());
  if (answers.timeline) factors.push("Timeline: " + labelOf(config.timelines, answers.timeline).toLowerCase());
  return factors;
}

// ---- Grounded preliminary advisor message ----
export function getPrelimMessage(preview) {
  const n = preview?.role_count || (preview?.top_roles ? preview.top_roles.length : 0);
  const word = NUM_WORDS[n] || `${n}-role`;
  if (!n) return "I've outlined a practical support structure focused on your highest-priority workload.";
  return `I found a practical ${word} structure that addresses your highest-priority workload without overbuilding the team.`;
}
